// /src/pages/private/members/MembersOnboardingSectionMedia.jsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';
import { onboardingBackgroundKind } from '../../../utils/onboardingLandingUi';

const SEEK_BACK_SECONDS = 10;

function clamp01(n) {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

function formatClock(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function IconPlayTriangle() {
  return (
    <svg
      className="members-onboarding-steps__video-iconSvg"
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
    >
      <polygon points="7,5 7,19 19,12" fill="currentColor" />
    </svg>
  );
}

function IconPauseBars() {
  return (
    <svg
      className="members-onboarding-steps__video-iconSvg"
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
    >
      <rect x="5" y="5" width="4" height="14" rx="1" fill="currentColor" />
      <rect x="15" y="5" width="4" height="14" rx="1" fill="currentColor" />
    </svg>
  );
}

/** Rewind 10 seconds (Carbon-style “rewind--10” glyph). */
function IconRewind10() {
  return (
    <svg
      className="members-onboarding-steps__video-iconSvg members-onboarding-steps__video-iconSvg--rewind"
      viewBox="0 0 32 32"
      aria-hidden
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M4,18A12,12,0,1,0,16,6H12V1L6,7l6,6V8h4A10,10,0,1,1,6,18Z"
      />
      <path
        fill="currentColor"
        d="M19.63,22.13a2.84,2.84,0,0,1-1.28-.27,2.44,2.44,0,0,1-.89-.77,3.57,3.57,0,0,1-.52-1.25,7.69,7.69,0,0,1-.17-1.68,7.83,7.83,0,0,1,.17-1.68,3.65,3.65,0,0,1,.52-1.25,2.44,2.44,0,0,1,.89-.77,2.84,2.84,0,0,1,1.28-.27,2.44,2.44,0,0,1,2.16,1,5.23,5.23,0,0,1,.7,2.93,5.23,5.23,0,0,1-.7,2.93A2.44,2.44,0,0,1,19.63,22.13Zm0-1.22a1.07,1.07,0,0,0,1-.55A3.38,3.38,0,0,0,21,18.85V17.47a3.31,3.31,0,0,0-.29-1.5,1.23,1.23,0,0,0-2.06,0,3.31,3.31,0,0,0-.29,1.5v1.38a3.38,3.38,0,0,0,.29,1.51A1.06,1.06,0,0,0,19.63,20.91Z"
      />
      <path
        fill="currentColor"
        d="M10.63,22V20.82h2V15.63l-1.86,1-.55-1.06,2.32-1.3H14v6.5h1.78V22Z"
      />
    </svg>
  );
}

function ImageDwellTimer({
  isValidationActive,
  dwellSeconds,
  onComplete,
  suppressInlineTimer = false,
  onRemainingSecondsTick,
}) {
  const duration = Number.isFinite(dwellSeconds) ? Math.max(0, Math.floor(dwellSeconds)) : 0;
  const [remaining, setRemaining] = useState(duration);
  const completedRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setRemaining(duration);
    completedRef.current = false;
  }, [duration]);

  useEffect(() => {
    if (typeof onRemainingSecondsTick !== 'function') return undefined;
    if (!isValidationActive || duration <= 0) {
      onRemainingSecondsTick(null);
      return undefined;
    }
    onRemainingSecondsTick(remaining);
    return undefined;
  }, [remaining, isValidationActive, duration, onRemainingSecondsTick]);

  useEffect(() => {
    if (!isValidationActive) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    if (completedRef.current) return undefined;

    if (duration <= 0) {
      if (!completedRef.current) {
        completedRef.current = true;
        queueMicrotask(onComplete);
      }
      return undefined;
    }

    intervalRef.current = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (!completedRef.current) {
            completedRef.current = true;
            onComplete();
          }
          return 0;
        }
        return r - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isValidationActive, duration, onComplete]);

  const progress = duration <= 0 ? 1 : 1 - remaining / duration;
  const circumference = 2 * Math.PI * 36;
  const dashOffset = circumference * (1 - progress);

  if (suppressInlineTimer) {
    return null;
  }

  return (
    <div className="members-onboarding-steps__image-timer" aria-live="polite">
      <svg className="members-onboarding-steps__image-timer-svg" viewBox="0 0 88 88" aria-hidden>
        <circle className="members-onboarding-steps__image-timer-track" cx="44" cy="44" r="36" />
        <circle
          className="members-onboarding-steps__image-timer-fill"
          cx="44"
          cy="44"
          r="36"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 44 44)"
        />
      </svg>
      <span className="members-onboarding-steps__image-timer-count">{remaining}</span>
      <span className="members-onboarding-steps__image-timer-hint">
        {remaining > 0 ? 'Keep viewing' : 'Done'}
      </span>
    </div>
  );
}

function RestrictedVideoPlayer({
  media,
  kind,
  srcUrl,
  isValidationActive,
  mediaPlaybackUnlocked,
  onEndedComplete,
}) {
  const videoWrapRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const furthestRef = useRef(0);
  const endedOnceRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  const preferMute = media?.hasAudio === false;

  useLayoutEffect(() => {
    const el = videoWrapRef.current;
    if (!el) return;
    const fx = clamp01(Number(media?.focalX));
    const fy = clamp01(Number(media?.focalY));
    el.style.setProperty('--mob-step-focal-x', `${fx * 100}%`);
    el.style.setProperty('--mob-step-focal-y', `${fy * 100}%`);
  }, [media]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !srcUrl) return undefined;

    if (kind === 'hls') {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = srcUrl;
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          startLevel: 0,
          autoStartLoad: true,
          capLevelToPlayerSize: false,
        });
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(srcUrl);
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data?.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            hls.destroy();
            hlsRef.current = null;
          }
        });
        hls.attachMedia(video);
        hlsRef.current = hls;
      }

      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        video.removeAttribute('src');
        video.load();
      };
    }

    if (kind === 'video') {
      video.src = srcUrl;
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    return undefined;
  }, [kind, srcUrl]);

  const clampSeekForward = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const max = furthestRef.current + 0.35;
    if (v.currentTime > max) {
      v.currentTime = max;
    }
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const onTimeUpdate = () => {
      furthestRef.current = Math.max(furthestRef.current, v.currentTime);
      setCurrent(v.currentTime);
    };

    const onSeeking = () => {
      clampSeekForward();
    };

    const onSeeked = () => {
      clampSeekForward();
    };

    const onLoadedMeta = () => {
      setDuration(Number.isFinite(v.duration) ? v.duration : 0);
      furthestRef.current = 0;
    };

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    const onEnded = () => {
      setPlaying(false);
      if (endedOnceRef.current) return;
      endedOnceRef.current = true;
      if (isValidationActive) {
        onEndedComplete();
      }
    };

    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('seeking', onSeeking);
    v.addEventListener('seeked', onSeeked);
    v.addEventListener('loadedmetadata', onLoadedMeta);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);

    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('seeking', onSeeking);
      v.removeEventListener('seeked', onSeeked);
      v.removeEventListener('loadedmetadata', onLoadedMeta);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
    };
  }, [clampSeekForward, isValidationActive, onEndedComplete, srcUrl]);

  useEffect(() => {
    endedOnceRef.current = false;
    furthestRef.current = 0;
  }, [srcUrl]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    if (!mediaPlaybackUnlocked) {
      v.pause();
    }
    return undefined;
  }, [mediaPlaybackUnlocked]);

  const seekBack = () => {
    if (!mediaPlaybackUnlocked) return;
    const v = videoRef.current;
    if (!v) return;
    const next = Math.max(0, v.currentTime - SEEK_BACK_SECONDS);
    v.currentTime = next;
    furthestRef.current = Math.min(furthestRef.current, next);
  };

  const togglePlay = () => {
    if (!mediaPlaybackUnlocked) return;
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
      return;
    }
    if (v.ended) {
      v.currentTime = 0;
    }
    v.play().catch(() => {});
  };

  return (
    <div className="members-onboarding-steps__restricted-video">
      <div ref={videoWrapRef} className="members-onboarding-steps__media-video-wrap">
        <video
          ref={videoRef}
          className="members-onboarding-steps__media-video"
          playsInline
          controls={false}
          muted={preferMute}
        />
      </div>
      <div className="members-onboarding-steps__video-controlBar">
        <button
          type="button"
          className="members-onboarding-steps__video-controlBtn"
          onClick={seekBack}
          disabled={!mediaPlaybackUnlocked}
          aria-label="Rewind 10 seconds"
          data-resource="members.onboarding.mediasstep"
          data-action="read"
          data-description="Onboarding step → Video rewind 10s"
        >
          <IconRewind10 />
        </button>
        <button
          type="button"
          className="members-onboarding-steps__video-controlBtn members-onboarding-steps__video-controlBtn--playPause"
          onClick={togglePlay}
          disabled={!mediaPlaybackUnlocked}
          aria-label={playing ? 'Pause' : 'Play'}
          data-resource="members.onboarding.mediasstep"
          data-action="read"
          data-description="Onboarding step → Video play or pause"
        >
          {playing ? <IconPauseBars /> : <IconPlayTriangle />}
        </button>
        <span className="members-onboarding-steps__video-time">
          {formatClock(current)} / {formatClock(duration)}
        </span>
      </div>
      {!mediaPlaybackUnlocked ? (
        <p className="members-onboarding-steps__video-locked-hint">Complete previous steps to watch.</p>
      ) : (
        <p className="members-onboarding-steps__video-hint">
          Forward seeking is disabled. Watch until the end to continue.
        </p>
      )}
    </div>
  );
}

/**
 * Onboarding card media with step validation rules (image dwell / restricted video).
 */
export default function MembersOnboardingSectionMedia({
  media,
  imageGallery = [],
  minimumDwellTotalSeconds = 0,
  isValidationActive,
  sectionCompleted = false,
  onValidationComplete,
  suppressImageTimer = false,
  onImageCountdownTick,
}) {
  const { t } = useTranslation();
  const stepCompletionRef = useRef(false);
  const kind = useMemo(() => onboardingBackgroundKind(media), [media]);
  const slides = useMemo(
    () => (Array.isArray(imageGallery) ? imageGallery : []),
    [imageGallery],
  );
  const slidesKey = useMemo(() => slides.map((s) => s.id).join('|'), [slides]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [visitedSlides, setVisitedSlides] = useState([]);
  const [timerFinished, setTimerFinished] = useState(false);
  const prevGalleryKeyRef = useRef('');

  const galleryBrowseEnabled = isValidationActive || sectionCompleted;

  useEffect(() => {
    if (!slides.length) {
      setSlideIndex(0);
      return;
    }
    setSlideIndex((i) => Math.min(Math.max(0, i), slides.length - 1));
  }, [slides]);

  useEffect(() => {
    if (!isValidationActive) {
      if (sectionCompleted && slides.length) {
        setTimerFinished(true);
        setVisitedSlides(slides.map(() => true));
        stepCompletionRef.current = true;
      } else {
        setTimerFinished(false);
        setVisitedSlides([]);
        stepCompletionRef.current = false;
        prevGalleryKeyRef.current = '';
      }
      return;
    }
    if (!slides.length) return;

    const galleryChanged = prevGalleryKeyRef.current !== slidesKey;
    if (galleryChanged) {
      prevGalleryKeyRef.current = slidesKey;
      stepCompletionRef.current = false;
      setTimerFinished(false);
      setVisitedSlides(slides.map((_, i) => i === slideIndex));
      return;
    }

    setVisitedSlides((prev) => {
      if (prev.length !== slides.length) {
        return slides.map((_, i) => i === slideIndex);
      }
      const next = [...prev];
      next[slideIndex] = true;
      return next;
    });
  }, [isValidationActive, sectionCompleted, slides, slidesKey, slideIndex, slides.length]);

  const handleTimerComplete = useCallback(() => {
    setTimerFinished(true);
  }, []);

  const allSlidesViewed =
    slides.length > 0 &&
    visitedSlides.length === slides.length &&
    visitedSlides.every(Boolean);

  useEffect(() => {
    if (!isValidationActive || !allSlidesViewed || !timerFinished || stepCompletionRef.current) {
      return;
    }
    stepCompletionRef.current = true;
    onValidationComplete();
  }, [allSlidesViewed, isValidationActive, onValidationComplete, timerFinished]);

  const currentImage = slides[slideIndex] || null;

  const mediaPlaybackUnlocked = Boolean(isValidationActive || sectionCompleted);

  const videoUrl = media?.url ? String(media.url).trim() : '';

  if (kind === 'hls' || kind === 'video') {
    if (!videoUrl) {
      return (
        <p className="members-onboarding-steps__empty members-onboarding-steps__empty--media">
          No media for this step yet.
        </p>
      );
    }
    return (
      <RestrictedVideoPlayer
        media={media}
        kind={kind}
        srcUrl={videoUrl}
        isValidationActive={isValidationActive}
        mediaPlaybackUnlocked={mediaPlaybackUnlocked}
        onEndedComplete={onValidationComplete}
      />
    );
  }

  if (!slides.length) {
    return (
      <p className="members-onboarding-steps__empty members-onboarding-steps__empty--media">
        No media for this step yet.
      </p>
    );
  }

  const displayUrl = currentImage?.url ? String(currentImage.url).trim() : '';
  if (!displayUrl) {
    return (
      <p className="members-onboarding-steps__empty members-onboarding-steps__empty--media">
        No media for this step yet.
      </p>
    );
  }

  const goPrev = () => {
    if (!galleryBrowseEnabled) return;
    setSlideIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (!galleryBrowseEnabled) return;
    setSlideIndex((i) => Math.min(slides.length - 1, i + 1));
  };

  const viewedCount = visitedSlides.filter(Boolean).length;
  const galleryProgressHint =
    slides.length > 1 && !allSlidesViewed
      ? t('View every image ({{seen}}/{{total}})', {
          seen: viewedCount,
          total: slides.length,
        })
      : slides.length > 1 && !timerFinished && minimumDwellTotalSeconds > 0
      ? t('Minimum viewing time in progress.')
      : null;

  return (
    <div className="members-onboarding-steps__media-image-block">
      <div className="members-onboarding-steps__media-layer">
        <img
          className="members-onboarding-steps__media-img"
          src={displayUrl}
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>

      <div
        className="members-onboarding-steps__media-pagination"
        role="navigation"
        aria-label="Image pages"
      >
        <button
          type="button"
          className="members-onboarding-steps__media-pageArrow"
          aria-label="Previous image"
          disabled={!galleryBrowseEnabled || slideIndex <= 0}
          onClick={goPrev}
          data-resource="members.onboarding.mediasstep"
          data-action="read"
          data-description="Onboarding step → Gallery previous"
        >
          &lt;
        </button>
        <div className="members-onboarding-steps__media-pageNums" role="tablist">
          {slides.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              role="tab"
              aria-selected={idx === slideIndex}
              aria-label={`Image ${idx + 1}`}
              className={`members-onboarding-steps__media-pageNum ${
                idx === slideIndex ? 'is-active' : ''
              } ${visitedSlides[idx] ? 'is-viewed' : ''}`}
              disabled={!galleryBrowseEnabled}
              onClick={() => setSlideIndex(idx)}
              data-resource="members.onboarding.mediasstep"
              data-action="read"
              data-description="Onboarding step → Gallery page"
            >
              {idx + 1}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="members-onboarding-steps__media-pageArrow"
          aria-label="Next image"
          disabled={!galleryBrowseEnabled || slideIndex >= slides.length - 1}
          onClick={goNext}
          data-resource="members.onboarding.mediasstep"
          data-action="read"
          data-description="Onboarding step → Gallery next"
        >
          &gt;
        </button>
      </div>

      {isValidationActive && slides.length > 1 && galleryProgressHint ? (
        <p className="members-onboarding-steps__media-galleryHint" role="status">
          {galleryProgressHint}
        </p>
      ) : null}

      {isValidationActive ? (
        <ImageDwellTimer
          key={`imgdwell-${slidesKey}-${minimumDwellTotalSeconds}`}
          isValidationActive={isValidationActive}
          dwellSeconds={minimumDwellTotalSeconds}
          onComplete={handleTimerComplete}
          suppressInlineTimer={suppressImageTimer}
          onRemainingSecondsTick={onImageCountdownTick}
        />
      ) : !sectionCompleted ? (
        <p className="members-onboarding-steps__media-locked-hint">Complete previous steps.</p>
      ) : null}
    </div>
  );
}
